import type { ReactElement } from 'react';
import { PostSort, type PostFormatDto, type PostStatusDto } from '@gr/core/content/posts/dto';
import { useT } from '../../../../app/di';
import { BareInput, BareSelect } from '../../../../ui-kit/Field/Field';
import { statusFilterGroups, statusFilterValue, withStatusFilter, type PostFilters as Filters } from '../model/post-filters';
import styles from './PostFilters.module.scss';

export interface PostFiltersProps {
  filters: Filters;
  statuses: readonly PostStatusDto[];
  /** Правила оформления блога; без правил фильтра по ним нет. */
  formats?: readonly PostFormatDto[];
  onChange(filters: Filters): void;
}

/** POST-UC-6: поиск, тег, статус или состояние целиком, правило оформления и порядок списка постов. */
export function PostFilters({ filters, statuses, formats = [], onChange }: PostFiltersProps): ReactElement {
  const { t } = useT();
  const set = (patch: Partial<Filters>): void => onChange({ ...filters, ...patch });
  return (
    <div className={styles.filters}>
      <BareInput label={t('posts.list.search')} placeholder={t('posts.list.search')} type="search" value={filters.q} onChange={(e) => set({ q: e.target.value })} />
      <BareInput label={t('posts.list.tag')} placeholder={`#${t('posts.list.tag')}`} value={filters.tag} onChange={(e) => set({ tag: e.target.value })} />
      <BareSelect
        label={t('posts.list.status')}
        value={statusFilterValue(filters)}
        onChange={(e) => onChange(withStatusFilter(filters, e.target.value))}
        options={[
          { value: '', label: t('posts.list.allStatuses') },
          ...statusFilterGroups(statuses).flatMap((g) => {
            const group = t(`posts.meta.${g.meta}`);
            const whole = g.metaOption === undefined ? [] : [{ value: g.metaOption, label: t('posts.list.allOfMeta', { meta: group }), group }];
            return [...whole, ...g.statuses.map((s) => ({ value: s.option, label: s.name, group }))];
          }),
        ]}
      />
      {formats.length > 0 ? (
        <BareSelect
          label={t('posts.list.format')}
          value={filters.formatId}
          onChange={(e) => set({ formatId: e.target.value })}
          options={[{ value: '', label: t('posts.list.allFormats') }, ...formats.map((f) => ({ value: f.id, label: f.name }))]}
        />
      ) : null}
      <BareSelect
        label={t('posts.list.sort')}
        value={filters.sort}
        onChange={(e) => set({ sort: e.target.value as PostSort })}
        options={[
          { value: PostSort.Updated, label: t('posts.list.sortUpdated') },
          { value: PostSort.Created, label: t('posts.list.sortCreated') },
        ]}
      />
    </div>
  );
}
